import React, { createContext, useContext, useState } from 'react';
import { RestaurantMenu, MenuItem, MenuCategory, loadMenu, saveMenu } from '../api/menu';

interface MenuState {
  menu: RestaurantMenu;
  updateInfo: (info: Partial<Pick<RestaurantMenu, 'restaurantName' | 'description' | 'currency'>>) => void;
  addCategory: (name: string, description?: string) => void;
  updateCategory: (id: string, updates: Partial<MenuCategory>) => void;
  deleteCategory: (id: string) => void;
  addItem: (item: Omit<MenuItem, 'id'>) => void;
  updateItem: (id: string, updates: Partial<MenuItem>) => void;
  deleteItem: (id: string) => void;
  toggleItemAvailability: (id: string) => void;
}

const Ctx = createContext<MenuState | null>(null);

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const [menu, setMenu] = useState<RestaurantMenu>(loadMenu);

  function update(updater: (m: RestaurantMenu) => RestaurantMenu) {
    setMenu(prev => saveMenu(updater(prev)));
  }

  function updateInfo(info: Partial<Pick<RestaurantMenu, 'restaurantName' | 'description' | 'currency'>>) {
    update(m => ({ ...m, ...info }));
  }

  function addCategory(name: string, description?: string) {
    const cat: MenuCategory = { id: crypto.randomUUID(), name, description, sortOrder: menu.categories.length };
    update(m => ({ ...m, categories: [...m.categories, cat] }));
  }

  function updateCategory(id: string, updates: Partial<MenuCategory>) {
    update(m => ({ ...m, categories: m.categories.map(c => c.id === id ? { ...c, ...updates } : c) }));
  }

  function deleteCategory(id: string) {
    update(m => ({
      ...m,
      categories: m.categories.filter(c => c.id !== id),
      items: m.items.filter(i => i.category !== id),
    }));
  }

  function addItem(item: Omit<MenuItem, 'id'>) {
    const newItem: MenuItem = { ...item, id: crypto.randomUUID() };
    update(m => ({ ...m, items: [...m.items, newItem] }));
  }

  function updateItem(id: string, updates: Partial<MenuItem>) {
    update(m => ({ ...m, items: m.items.map(i => i.id === id ? { ...i, ...updates } : i) }));
  }

  function deleteItem(id: string) {
    update(m => ({ ...m, items: m.items.filter(i => i.id !== id) }));
  }

  function toggleItemAvailability(id: string) {
    update(m => ({ ...m, items: m.items.map(i => i.id === id ? { ...i, available: !i.available } : i) }));
  }

  return (
    <Ctx.Provider value={{ menu, updateInfo, addCategory, updateCategory, deleteCategory, addItem, updateItem, deleteItem, toggleItemAvailability }}>
      {children}
    </Ctx.Provider>
  );
}

export const useMenu = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMenu must be inside MenuProvider');
  return ctx;
};
